import { z } from 'zod'

import {
  EMAIL_MAX_LENGTH,
  isEmailLocalPartTooLong,
} from '@/utils/validate-email'

const basicData = z.object({
  nome_propriedade: z
    .string()
    .min(1, 'Digite o nome da propriedade.')
    .max(70, 'Nome da propriedade deve ter 70 caracteres no máximo.'),
  nome_resumido: z
    .string()
    .max(20, 'Nome resumido deve ter 20 caracteres no máximo.'),
  sobre_propriedade: z
    .string()
    .min(1, 'Esse campo é obrigatório.')
    .max(5000, 'Sobre a propriedade deve ter 5000 caracteres no máximo.'),
  sobre_objetivo: z
    .string()
    .min(1, 'Esse campo é obrigatório')
    .max(2000, 'Objetivo da propriedade deve ter 2000 caracteres no máximo.'),
  nome_proprietario: z
    .string()
    .min(1, 'Digite o nome do proprietário(a).')
    .max(70, 'Nome do proprietário deve ter 70 caracteres no máximo.'),
  nome_gestor: z
    .string()
    .min(1, 'Digite o nome do gestor.')
    .max(70, 'Nome do gestor deve ter 70 caracteres no máximo.'),
})

const address = z.object({
  bioma: z.string().min(1, 'Selecione o bioma da propriedade.'),
  regiao: z.string().min(1, 'Selecione a região da propriedade.'),
  estado: z.string().min(1, 'Selecione o estado da propriedade.'),
  cidade: z.string().min(1, 'Selecione a cidade da propriedade.'),
  ano_criacao: z
    .string()
    .min(1, 'Selecione o ano de criação.')
    .refine(
      (data) => {
        const currentYear = new Date().getFullYear()
        if (data > String(currentYear)) {
          return false
        }
        return true
      },
      {
        message: 'O ano de criação não pode ser maior que a ano atual.',
      },
    ),
  area_preservada: z
    .string()
    .min(1, 'Selecione a área preservada.')
    .max(7, 'A área deve ter 7 caracteres no máximo'),
  tipos_preservacao: z.array(z.string()),
  unidade_area: z.string().default('ha'),
})

const bankData = z.object({
  id_conta: z.string().optional(),
})

const objectRppn = z
  .object({
    concierge: z
      .string()
      .max(150, 'Portaria deve ter no máximo 150 caracteres.')
      .optional(),
    darius: z
      .string()
      .max(150, 'Diário Oficial deve ter no máximo 150 caracteres.')
      .optional(),
    date: z.string().optional(),
    sectionAndPage: z
      .string()
      .max(150, 'Seção e página deve ter no máximo 150 caracteres.')
      .optional(),
  })
  .superRefine((data, ctx) => {
    const { concierge, darius, date, sectionAndPage } = data

    const isAnyFieldFilled = concierge || darius || date || sectionAndPage

    if (isAnyFieldFilled) {
      if (!concierge) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Digite uma portaria.',
          path: ['concierge'],
        })
      }
      if (!darius) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Digite o diário oficial.',
          path: ['darius'],
        })
      }
      if (!date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Selecione uma data.',
          path: ['date'],
        })
      }
      if (!sectionAndPage) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Digite a seção e página.',
          path: ['sectionAndPage'],
        })
      }
    }
  })

export const contractSignatures = z
  .object({
    nome: z
      .string()
      .min(1, 'Nome é obrigatório.')
      .max(255, 'O nome deve ter 255 caracteres no máximo.'),
    cpf: z.string().regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'CPF inválido.'),
    email: z
      .string()
      .email('Digite um e-mail válido.')
      .min(1, 'O e-mail é obrigatório.')
      .max(
        EMAIL_MAX_LENGTH,
        `O e-mail deve ter ${EMAIL_MAX_LENGTH} caracteres no máximo.`,
      )
      .regex(
        /^[a-zA-Z0-9.$/-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'O e-mail contém caracteres inválidos.',
      )
      .superRefine((email, ctx) => {
        const { isTooLong, maxLength } = isEmailLocalPartTooLong({
          email,
        })

        if (isTooLong) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `O email antes do @ deve ter no máximo ${maxLength} caracteres.`,
          })
        }
      }),
    id_funcao: z.string().optional(),
    incluido_no_anexo: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (data.incluido_no_anexo && !data.id_funcao) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['id_funcao'],
        message: 'Função é obrigatória.',
      })
    }
  })

export type ContractSignaturesSchema = z.infer<typeof contractSignatures>

const numeroCar = z.object({
  value: z
    .string()
    .max(50, 'O número CAR deve ter no máximo 50 caracteres.')
    .optional()
    .refine(
      (value) =>
        !value || /^[A-Za-z]{2}[-][0-9]{7}[-][A-Za-z0-9]{32}$/.test(value),
      {
        message: 'Número CAR inválido.',
      },
    ),
})

const numeroCib = z.object({
  value: z
    .string()
    .max(9, 'O número CIB deve ter 9 caracteres.')
    .optional()
    .refine((value) => !value || /^[A-Za-z0-9]{7}-[A-Za-z0-9]$/.test(value), {
      message: 'Número CIB inválido.',
    }),
})

export const propertyFormValidationSchema = z
  .object({
    propriedade: z.object({
      escala: z.string().optional(),
      carbono: z.boolean().default(false),
      dadosbasicos: basicData,
      endereco: address,
      // Coordenadas hidrograficas e geograficas
      tipo_coordenada_geografica: z.enum(['Polar', 'Decimal']).default('Polar'),
      geografica_lat_orientacao: z.string().default(''),
      geografica_lat_graus: z.string().optional().nullable(),
      geografica_lat_minutos: z.string().optional().nullable(),
      geografica_lat_segundos: z.string().optional().nullable(),
      geografica_lng_orientacao: z.string().default(''),
      geografica_lng_graus: z.string().optional().nullable(),
      geografica_lng_minutos: z.string().optional().nullable(),
      geografica_lng_segundos: z.string().optional().nullable(),
      geografica_lat_decimal: z.string().optional().nullable(),
      geografica_lng_decimal: z.string().optional().nullable(),
      nome_bacia_hidrografica: z
        .string()
        .max(50, 'O nome da bacia deve ter 50 caracteres no máximo')
        .optional()
        .nullable(),
      emissoes_internas: z
        .string()
        .max(5, 'Emissões internas deve deve ser entre 0.00 e 99.99')
        .optional()
        .nullable(),
      tipo_propriedade: z.object({
        tipo: z.string().min(1, 'Esse campo é obrigatório').default('Privado'),
      }),

      rppn: z.array(objectRppn),

      numero_car: z.array(numeroCar),

      numero_cib: z
        .array(numeroCib)
        .optional()
        .default([{ value: '' }]),

      taxa_preservacao: z
        .string()
        .max(26, 'O valor não pode ser maior que R$ 1.000.000.000.000.000,00.'),
      status: z.number().default(1),
      tipo_conservacao: z.string().min(1, 'Esse campo é obrigatório.'),
    }),
    dados_bancarios: bankData,
    assinantes_contrato_propriedade: z.array(contractSignatures),
  })
  .refine(
    (data) => {
      const {
        taxa_preservacao: preservationRate,
        tipo_conservacao: conservationType,
      } = data.propriedade
      const isRequired = conservationType !== 'R' && conservationType !== 'M'
      if (isRequired && !preservationRate) {
        return false
      }
      return true
    },
    {
      path: ['propriedade.taxa_preservacao'],
      message: 'Esse campo é obrigatório.',
    },
  )
  .refine(
    (data) => {
      const tipo = data.propriedade.tipo_coordenada_geografica
      const latGraus = data.propriedade.geografica_lat_graus

      if (tipo === 'Polar') {
        const isLatFilled = latGraus

        if (isLatFilled) {
          const latValido =
            latGraus &&
            !isNaN(parseFloat(latGraus)) &&
            parseFloat(latGraus) >= 0 &&
            parseFloat(latGraus) <= 85

          return latValido
        }
      }

      return true
    },
    {
      message: 'Graus deve estar entre 0 a 85',
      path: ['propriedade.geografica_lat_graus'],
    },
  )
  .refine(
    (data) => {
      const tipo = data.propriedade.tipo_coordenada_geografica
      const latMinutos = data.propriedade.geografica_lat_minutos

      if (tipo === 'Polar') {
        const isLatFilled = latMinutos

        if (isLatFilled) {
          const minValido =
            !latMinutos ||
            (!isNaN(parseFloat(latMinutos)) &&
              parseFloat(latMinutos) >= 0 &&
              parseFloat(latMinutos) <= 59)

          return minValido
        }
      }

      return true
    },
    {
      message: 'Minutos deve estar entre 0 a 59',
      path: ['propriedade.geografica_lat_minutos'],
    },
  )
  .refine(
    (data) => {
      const tipo = data.propriedade.tipo_coordenada_geografica
      const latSegundos = data.propriedade.geografica_lat_segundos

      if (tipo === 'Polar') {
        const isLatFilled = latSegundos

        if (isLatFilled) {
          const segValido =
            !latSegundos ||
            (!isNaN(parseFloat(latSegundos)) &&
              parseFloat(latSegundos) >= 0 &&
              parseFloat(latSegundos) <= 59.9999999)

          return segValido
        }
      }

      return true
    },
    {
      message: 'Segundos deve estar entre 0 a 59.9999999',
      path: ['propriedade.geografica_lat_segundos'],
    },
  )
  .refine(
    (data) => {
      const tipo = data.propriedade.tipo_coordenada_geografica
      const lngGraus = data.propriedade.geografica_lng_graus

      if (tipo === 'Polar') {
        const isLngFilled = lngGraus

        if (isLngFilled) {
          const lngValido =
            lngGraus &&
            !isNaN(parseFloat(lngGraus)) &&
            parseFloat(lngGraus) >= 0 &&
            parseFloat(lngGraus) <= 180

          return lngValido
        }
      }

      return true
    },
    {
      message: 'Graus deve estar entre 0 a 180',
      path: ['propriedade.geografica_lng_graus'],
    },
  )
  .refine(
    (data) => {
      const tipo = data.propriedade.tipo_coordenada_geografica
      const lngMinutos = data.propriedade.geografica_lng_minutos

      if (tipo === 'Polar') {
        const isLngFilled = lngMinutos

        if (isLngFilled) {
          const minValido =
            !lngMinutos ||
            (!isNaN(parseFloat(lngMinutos)) &&
              parseFloat(lngMinutos) >= 0 &&
              parseFloat(lngMinutos) <= 59)

          return minValido
        }
      }

      return true
    },
    {
      message: 'Minutos deve estar entre 0 a 59',
      path: ['propriedade.geografica_lng_minutos'],
    },
  )
  .refine(
    (data) => {
      const tipo = data.propriedade.tipo_coordenada_geografica
      const lngSegundos = data.propriedade.geografica_lng_segundos

      if (tipo === 'Polar') {
        const isLngFilled = lngSegundos

        if (isLngFilled) {
          const segValido =
            !lngSegundos ||
            (!isNaN(parseFloat(lngSegundos)) &&
              parseFloat(lngSegundos) >= 0 &&
              parseFloat(lngSegundos) <= 59.9999999)

          return segValido
        }
      }

      return true
    },
    {
      message: 'Segundos deve estar entre 0 a 59.9999999',
      path: ['propriedade.geografica_lng_segundos'],
    },
  )
  .refine(
    (data) => {
      const tipo = data.propriedade.tipo_coordenada_geografica
      const latDecimal = data.propriedade.geografica_lat_decimal
      if (
        tipo === 'Decimal' &&
        latDecimal !== null &&
        latDecimal !== '' &&
        typeof latDecimal === 'string'
      ) {
        const latValido =
          !isNaN(parseFloat(latDecimal)) &&
          parseFloat(latDecimal) >= -85 &&
          parseFloat(latDecimal) <= 85

        return latValido
      }
      return true
    },
    {
      message: 'Latitude deve estar entre -85 a 85',
      path: ['propriedade.geografica_lat_decimal'],
    },
  )
  .refine(
    (data) => {
      const tipo = data.propriedade.tipo_coordenada_geografica
      const lngDecimal = data.propriedade.geografica_lng_decimal
      if (
        tipo === 'Decimal' &&
        lngDecimal !== null &&
        lngDecimal !== '' &&
        typeof lngDecimal === 'string'
      ) {
        const lngValido =
          !isNaN(parseFloat(lngDecimal)) &&
          parseFloat(lngDecimal) >= -180 &&
          parseFloat(lngDecimal) <= 180

        return lngValido
      }
      return true
    },
    {
      message: 'Longitude deve estar entre -180 a 180',
      path: ['propriedade.geografica_lng_decimal'],
    },
  )
  .refine(
    (data) => {
      const bacia = data.propriedade
      if (bacia.tipo_coordenada_geografica === 'Polar') {
        const hasCoordinates =
          bacia.geografica_lat_graus ||
          bacia.geografica_lat_minutos ||
          bacia.geografica_lat_segundos

        if (hasCoordinates && !bacia.geografica_lat_orientacao) {
          return false
        }
      }

      return true
    },
    {
      message: 'Selecione um ponto cardeal',
      path: ['propriedade.geografica_lat_orientacao'],
    },
  )
  .refine(
    (data) => {
      const bacia = data.propriedade
      if (bacia.tipo_coordenada_geografica === 'Polar') {
        const hasCoordinates =
          bacia.geografica_lng_graus ||
          bacia.geografica_lng_minutos ||
          bacia.geografica_lng_segundos

        if (hasCoordinates && !bacia.geografica_lng_orientacao) {
          return false
        }
      }

      return true
    },
    {
      message: 'Selecione um ponto cardeal',
      path: ['propriedade.geografica_lng_orientacao'],
    },
  )
  .superRefine((data, ctx) => {
    const tipo = data.propriedade.tipo_coordenada_geografica

    if (tipo === 'Polar') {
      // Valida a Latitude
      const {
        geografica_lat_graus: latGraus,
        geografica_lat_minutos: latMinutos,
        geografica_lat_segundos: latSegundos,
        geografica_lat_orientacao: latOrientacao,
      } = data.propriedade

      if (latGraus || latMinutos || latSegundos || latOrientacao) {
        if (!latGraus) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['propriedade', 'geografica_lat_graus'],
            message: 'Preencha esse campo.',
          })
        }
        if (!latMinutos) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['propriedade', 'geografica_lat_minutos'],
            message: 'Preencha esse campo.',
          })
        }
        if (!latSegundos) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['propriedade', 'geografica_lat_segundos'],
            message: 'Preencha esse campo.',
          })
        }
        if (!latOrientacao) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['propriedade', 'geografica_lat_orientacao'],
            message: 'Preencha esse campo.',
          })
        }
      }

      // Valida Longitude
      const {
        geografica_lng_graus: lngGraus,
        geografica_lng_minutos: lngMinutos,
        geografica_lng_segundos: lngSegundos,
        geografica_lng_orientacao: lngOrientacao,
      } = data.propriedade

      if (lngGraus || lngMinutos || lngSegundos || lngOrientacao) {
        if (!lngGraus) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['propriedade', 'geografica_lng_graus'],
            message: 'Preencha esse campo.',
          })
        }
        if (!lngMinutos) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['propriedade', 'geografica_lng_minutos'],
            message: 'Preencha esse campo.',
          })
        }
        if (!lngSegundos) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['propriedade', 'geografica_lng_segundos'],
            message: 'Preencha esse campo.',
          })
        }
        if (!lngOrientacao) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['propriedade', 'geografica_lng_orientacao'],
            message: 'Preencha esse campo.',
          })
        }
      }
    }

    if (tipo === 'Decimal') {
      // Valida Coordenada Geograficas Decimal
      const {
        geografica_lat_decimal: geoLatDecimal,
        geografica_lng_decimal: geoLngDecimal,
      } = data.propriedade

      if (geoLatDecimal || geoLngDecimal) {
        if (!geoLatDecimal) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['propriedade', 'geografica_lat_decimal'],
            message: 'Preencha esse campo.',
          })
        }
        if (!geoLngDecimal) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['propriedade', 'geografica_lng_decimal'],
            message: 'Preencha esse campo.',
          })
        }
      }
    }
  })

export type PropertyFormValues = z.infer<typeof propertyFormValidationSchema>
